<template>
  <!-- Apertura / cierre de caja compartido por salesCounter y salesQuick. -->
  <div class="sc-cash-session">
    <!-- ── APERTURA / CIERRE DE CAJA ── -->
    <div v-if="visible" class="sc-caja-screen">
      <b-row class="sc-caja-row">
        <b-col v-if="cajaFlow.mode === 'cierre' && queueHasBlockingItemsForCierre" cols="12">
          <b-alert show variant="warning" class="mb-3">
            <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
            No se puede cerrar caja: hay comprobante(s) sin sincronizar para esta caja por
            {{ formatCurrency(queueOpenTotalForCierre) }}.
            <b-button size="sm" variant="outline-dark" class="ml-2" @click="showQueueErrors()">
              Ver cola
            </b-button>
          </b-alert>
        </b-col>
        <b-col v-if="cajaNdMismatch" cols="12">
          <b-alert show variant="warning" class="mb-3">
            <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
            La caja <strong>{{ cajaFlow.cajas[0].name }}</strong> no corresponde al tipo de comprobantes del punto de venta
            actual: no va a aparecer como medio de cobro. Revisá la caja asignada o el punto de venta.
          </b-alert>
        </b-col>
        <b-col v-for="caja in cajaFlow.cajas" :key="caja.id"
               md="12"
               class="mb-3 sc-caja-col">
          <b-card
            :border-variant="!caja.only_open ? 'info' : (caja.opening ? 'success' : 'danger')"
            body-class="sc-caja-body"
            :class="['sc-caja-card', !caja.only_open ? 'sc-caja-card-info' : (caja.opening ? 'sc-caja-card-open' : 'sc-caja-card-closed')]">
            <template #header>
              <div class="sc-caja-mode-label">
                <b-icon :icon="cajaFlow.mode === 'apertura' ? 'lock-fill' : 'cash-stack'" class="mr-1"></b-icon>
                {{ cajaFlow.mode === 'apertura' ? 'Apertura de Caja' : 'Estado de Caja' }}
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span><b-icon icon="cash-stack" class="mr-1"></b-icon> {{ caja.name }}</span>
                <b-badge :variant="!caja.only_open ? 'info' : (caja.opening ? 'success' : 'danger')">
                  <b-icon :icon="!caja.only_open ? 'cash-stack' : (caja.opening ? 'unlock-fill' : 'lock-fill')" class="mr-1"></b-icon>
                  {{ !caja.only_open ? 'SIN APERTURA' : (caja.opening ? 'ABIERTA' : 'CERRADA') }}
                </b-badge>
              </div>
            </template>

            <!-- Contenido scrollable -->
            <div class="sc-caja-content">
              <div class="sc-saldo-block mb-3">
                <div v-if="showSaldoPeriodo(caja)" class="sc-saldo-split">
                  <div>
                    <div class="sc-saldo-label">{{ cajaFlow.mode === 'consulta' ? 'Saldo del día' : 'Saldo del turno' }}</div>
                    <div class="sc-saldo-value">{{ formatCurrency(saldoPeriodo(caja)) }}</div>
                  </div>
                  <div>
                    <div class="sc-saldo-label">Saldo total en caja</div>
                    <div class="sc-saldo-value">{{ formatCurrency(caja.total_available_ars) }}</div>
                  </div>
                </div>
                <template v-else>
                  <div class="sc-saldo-label">Saldo total en caja</div>
                  <div class="sc-saldo-value">{{ formatCurrency(caja.total_available_ars) }}</div>
                </template>
              </div>

              <template v-if="caja.opening || cajaFlow.mode === 'consulta'">
                <div v-if="cajaFlow.resumen[caja.id] && cajaFlow.resumen[caja.id].loading"
                     class="text-center py-2 text-muted small">
                  <b-spinner small class="mr-1"></b-spinner> Cargando movimientos...
                </div>
                <template v-else-if="cajaFlow.resumen[caja.id]">
                  <b-alert v-if="cajaFlow.resumen[caja.id].resumenError"
                           show variant="warning" class="py-2 small">
                    <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
                    {{ cajaFlow.resumen[caja.id].resumenError }}
                  </b-alert>
                  <div class="small font-weight-bold text-muted mb-2">
                    {{ cajaFlow.mode === 'consulta' ? 'Movimientos del día' : 'Movimientos del turno' }}
                    <span class="sc-cierre-date ml-1">({{ formatCajaResumenDate(cajaFlow.resumen[caja.id].movimientos) }})</span>
                  </div>
                  <div v-if="cajaFlow.resumen[caja.id].movimientos.length" class="sc-cierre-cpbt-list">
                    <div class="sc-cierre-cpbt-header sc-caja-mov-grid">
                      <span>Hora</span>
                      <span>Concepto</span>
                      <span class="text-left">Cliente / proveedor</span>
                      <span class="text-right">Importe</span>
                    </div>
                    <div v-for="mov in cajaFlow.resumen[caja.id].movimientos" :key="mov.id"
                         class="sc-cierre-cpbt-row sc-caja-mov-grid">
                      <span class="text-muted">{{ formatDateTimeHMS(mov.date_time) }}</span>
                      <span>{{ mov.reference }}</span>
                      <span v-if="cajaMovimientoContraparte(mov)"
                            class="sc-caja-counterparty text-left"
                            :class="'sc-caja-counterparty--' + cajaMovimientoContraparteTipo(mov)">
                        <b-avatar v-if="cajaMovimientoContraparteImage(mov)"
                                  :src="cajaMovimientoContraparteImage(mov)" size="26px" />
                        <b-avatar v-else size="26px"
                                  :text="avatarFromName(cajaMovimientoContraparte(mov)).text"
                                  :style="avatarFromName(cajaMovimientoContraparte(mov)).style" />
                        <span>
                          <small class="sc-caja-counterparty__type">{{ cajaMovimientoContraparteTipo(mov) }}</small>
                          <span class="sc-caja-counterparty__name">{{ cajaMovimientoContraparte(mov) }}</span>
                        </span>
                      </span>
                      <span v-else class="text-muted">—</span>
                      <span class="sc-cierre-cpbt-total"
                            :class="mov.type_balance === '-' ? 'text-danger' : 'text-success'">
                        {{ mov.type_balance === '-' ? '-' : '+' }} {{ formatCurrency(mov.amount) }}
                      </span>
                    </div>
                  </div>
                  <div v-else class="text-muted small text-center py-2">
                    {{ cajaFlow.mode === 'consulta' ? 'Sin movimientos registrados hoy' : 'Sin movimientos registrados en este turno' }}
                  </div>
                </template>
              </template>
            </div>

            <!-- Acciones fijas al bottom del viewport -->
            <div class="sc-action-grid">
              <!-- Ir a Facturar — PRIMERO (izquierda) en estado de caja -->
              <div v-if="cajaFlow.mode === 'consulta' || (cajaFlow.mode === 'cierre' && caja.opening)"
                   class="sc-action-block sc-action-outline"
                   @click="setVisible(false)">
                <b-icon icon="arrow-left" class="sc-action-icon"></b-icon>
                <div class="sc-action-title">Ir a Facturar</div>
                <div class="sc-action-sub">Continuar con las ventas</div>
              </div>
              <!-- Caja cerrada -->
              <template v-if="caja.only_open && !caja.opening">
                <div class="sc-action-block sc-action-success"
                     :class="{ 'sc-action-disabled': cajaFlow.loading }"
                     @click="!cajaFlow.loading && confirmarAperturaCaja(caja)">
                  <b-icon icon="unlock-fill" class="sc-action-icon"></b-icon>
                  <div class="sc-action-title">Abrir caja</div>
                  <div class="sc-action-sub">Iniciar nuevo turno</div>
                </div>
              </template>
              <!-- Caja abierta -->
              <template v-if="caja.only_open && caja.opening">
                <div class="sc-action-block sc-action-money"
                     :class="{ 'sc-action-disabled': cajaFlow.loading }"
                     @click="!cajaFlow.loading && abrirModalAgregarDinero(caja)">
                  <b-icon icon="plus-circle-fill" class="sc-action-icon"></b-icon>
                  <div class="sc-action-title">Agregar dinero</div>
                  <div class="sc-action-sub">Transferir fondos a esta caja</div>
                </div>
                <div v-if="caja.renders_to_cash_id && tieneSaldoParaRendir(caja)"
                     class="sc-action-block sc-action-danger"
                     :class="{ 'sc-action-disabled': cajaFlow.loading || queueHasBlockingItemsForCaja(caja) }"
                     @click="!cajaFlow.loading && !queueHasBlockingItemsForCaja(caja) && abrirModalRendicion(caja)">
                  <b-icon icon="arrow-up-right-circle-fill" class="sc-action-icon"></b-icon>
                  <div class="sc-action-title">Cerrar y rendir</div>
                  <div class="sc-action-sub">Finalizar turno y transferir efectivo</div>
                </div>
                <div v-if="!caja.renders_to_cash_id || !tieneSaldoParaRendir(caja)"
                     class="sc-action-block sc-action-danger"
                     :class="{ 'sc-action-disabled': cajaFlow.loading || queueHasBlockingItemsForCaja(caja) }"
                     @click="!cajaFlow.loading && !queueHasBlockingItemsForCaja(caja) && confirmarCierreCaja(caja)">
                  <b-icon icon="lock-fill" class="sc-action-icon"></b-icon>
                  <div class="sc-action-title">Cerrar caja</div>
                  <div class="sc-action-sub">Finalizar el turno</div>
                </div>
              </template>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- ── MODAL: Confirmar apertura de caja ── -->
    <b-modal v-model="cajaFlow.modalApertura.active"
             no-close-on-backdrop hide-header-close
             size="md">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="unlock-fill" /></span>
        <h5 class="app-modal-header__title">Abrir caja — {{ cajaFlow.modalApertura.caja && cajaFlow.modalApertura.caja.name }}</h5>
      </div>
      <b-alert show variant="info" class="mb-3">
        <b-icon icon="info-circle-fill" class="mr-1"></b-icon>
        Se va a <strong>abrir</strong> la caja <strong>{{ cajaFlow.modalApertura.caja && cajaFlow.modalApertura.caja.name }}</strong>
        e iniciará un nuevo turno. A partir de este momento se registrarán todos los movimientos del turno.
      </b-alert>
      <p class="text-muted mb-0">
        Si necesitás agregar un fondo inicial, podés hacerlo una vez que la caja esté abierta con el botón <strong>Agregar dinero</strong>.
      </p>
      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1"
                  @click="cajaFlow.modalApertura.active = false">Cancelar</b-button>
        <b-button variant="success" @click="cajaFlow.modalApertura.active = false; abrirCaja(cajaFlow.modalApertura.caja)">
          <b-icon icon="unlock-fill" class="mr-1"></b-icon>Abrir caja
        </b-button>
      </div>
    </b-modal>

    <!-- ── MODAL: Confirmar cierre de caja (sin caja principal) ── -->
    <b-modal v-model="cajaFlow.modalCierre.active"
             no-close-on-backdrop hide-header-close
             size="md">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="lock-fill" /></span>
        <h5 class="app-modal-header__title">Cerrar caja — {{ cajaFlow.modalCierre.caja && cajaFlow.modalCierre.caja.name }}</h5>
      </div>
      <b-alert show variant="warning" class="mb-3">
        <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
        Se va a <strong>cerrar</strong> la caja <strong>{{ cajaFlow.modalCierre.caja && cajaFlow.modalCierre.caja.name }}</strong>
        y finalizará el turno. Para volver a facturar con esta caja vas a tener que abrirla de nuevo.
      </b-alert>
      <p v-if="cajaFlow.modalCierre.sinRendir" class="text-muted mb-0">
        <strong>No vas a rendir nada</strong> a la caja principal: el saldo
        ({{ formatCurrency(cajaFlow.modalCierre.caja && cajaFlow.modalCierre.caja.total_available_ars) }}) queda en la caja para el próximo turno.
      </p>
      <p v-else class="text-muted mb-0">
        El saldo de la caja no se transfiere: queda en la caja para el próximo turno.
      </p>
      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1"
                  @click="cajaFlow.modalCierre.active = false">Cancelar</b-button>
        <b-button variant="danger" @click="cajaFlow.modalCierre.active = false; cerrarCaja(cajaFlow.modalCierre.caja, 0)">
          <b-icon icon="lock-fill" class="mr-1"></b-icon>Cerrar caja
        </b-button>
      </div>
    </b-modal>

    <!-- ── MODAL: Agregar dinero a caja ── -->
    <b-modal v-model="cajaFlow.modalDinero.active"
             no-close-on-backdrop hide-header-close
             header-text-variant="white" size="md"
             header-class="sc-modal-money-header">
      <div slot="modal-header">
        <b-icon icon="plus-circle-fill" class="mr-2"></b-icon>
        Agregar dinero — {{ cajaFlow.modalDinero.caja && cajaFlow.modalDinero.caja.name }}
      </div>
      <b-alert v-if="cajaFlow.modalDinero.caja && cajaFlow.modalDinero.caja.renders_to_cash_id" show variant="info" class="mb-3">
        <b-icon icon="arrow-right-circle" class="mr-1"></b-icon>
        Se va a registrar una <strong>transferencia</strong> desde
        <strong>{{ (cajaFlow.allCajas.find(c => c.id === cajaFlow.modalDinero.caja.renders_to_cash_id) || {}).name }}</strong>
        hacia <strong>{{ cajaFlow.modalDinero.caja.name }}</strong>.
      </b-alert>
      <b-alert v-else show variant="secondary" class="mb-3">
        Se va a registrar un ingreso de dinero manual en la caja
        <strong>{{ cajaFlow.modalDinero.caja && cajaFlow.modalDinero.caja.name }}</strong>.
      </b-alert>
      <b-row>
        <b-col md="12">
          <b-form-group label="Importe a agregar">
            <b-input-group prepend="$">
              <b-form-input v-model="cajaFlow.modalDinero.amount"
                            type="number" step="0.01" min="0" ref="inputDinero" />
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Referencia">
            <b-form-input type="text" size="sm"
                          v-model="cajaFlow.modalDinero.reference"
                          placeholder="Ej: Fondo inicial" />
          </b-form-group>
        </b-col>
      </b-row>
      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1"
                  @click="cajaFlow.modalDinero.active = false">Cancelar</b-button>
        <b-button variant="dark" class="sc-btn-money" @click="guardarDineroCaja()">Confirmar</b-button>
      </div>
    </b-modal>

    <!-- ── MODAL: Cerrar y rendir caja ── -->
    <b-modal v-model="cajaFlow.modalRendicion.active"
             no-close-on-backdrop hide-header-close
             header-bg-variant="danger" header-text-variant="white" size="md">
      <div slot="modal-header">
        <b-icon icon="arrow-up-right-circle-fill" class="mr-2"></b-icon>
        Cerrar y rendir — {{ cajaFlow.modalRendicion.caja && cajaFlow.modalRendicion.caja.name }}
      </div>
      <div class="sc-saldo-block mb-3">
        <div class="sc-saldo-label">Saldo disponible en caja</div>
        <div class="sc-saldo-value">{{ formatCurrency(cajaFlow.modalRendicion.caja && cajaFlow.modalRendicion.caja.total_available_ars) }}</div>
      </div>
      <b-alert show variant="warning" class="mb-3">
        <b-icon icon="info-circle-fill" class="mr-1"></b-icon>
        La caja se va a <strong>cerrar</strong> y el efectivo rendido se va a
        <strong>transferir a la caja principal</strong>.
        No podés rendir más de lo disponible.
      </b-alert>
      <b-row>
        <b-col md="12">
          <b-form-group label="Importe a rendir">
            <b-input-group prepend="$">
              <b-form-input v-model="cajaFlow.modalRendicion.amount"
                            type="number" step="0.01" min="0" ref="inputRendicion" />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1"
                  @click="cajaFlow.modalRendicion.active = false">Cancelar</b-button>
        <b-button variant="danger" @click="guardarRendicion()">
          <b-icon icon="lock-fill" class="mr-1"></b-icon>Cerrar y rendir
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
  import serviceAPI from '../services'
  import ErrorHandler from '@/handler/error'
  import Funciones from '@/handler/funciones'
  import moment from 'moment'

  export default {
    name: 'CashSession',
    props: {
      // Pantalla de apertura/estado visible (v-bind.sync con la vista).
      visible: { type: Boolean, default: false },
      assignedCashId: { type: Number, default: 0 },
      // Comprobantes sin sincronizar que bloquean el cierre (sólo salesCounter tiene cola).
      isCashBlocked: { type: Function, default: () => false },
      openItemsForCashIds: { type: Function, default: () => [] },
      queueState: { default: null },
      // nd del punto de venta actual; null cuando no aplica (modo libre o sin punto).
      pointSaleNd: { default: null },
    },

    data: () => ({
      cajaFlow: {
        loading: false,
        mode: 'apertura',
        cajas: [],
        allCajas: [],
        resumen: {},
        modalApertura: { active: false, caja: null },
        modalCierre: { active: false, caja: null, sinRendir: false },
        modalDinero: { active: false, caja: null, amount: 0, reference: '' },
        modalRendicion: { active: false, caja: null, amount: 0 },
      },
    }),

    computed: {
      hasAssignedCash() {
        return this.assignedCashId > 0
      },
      queueHasBlockingItemsForCierre() {
        return this.queueOpenItemsForCierre.length > 0
      },
      queueOpenItemsForCierre() {
        this.queueState // eslint-disable-line no-unused-expressions -- la cola no es reactiva: recalcular cuando cambia
        const ids = (this.cajaFlow.cajas || []).map(c => c.id)
        return this.openItemsForCashIds(ids)
      },
      cajaNdMismatch() {
        const caja = this.cajaFlow.cajas[0]
        if (!caja || this.pointSaleNd === null || this.pointSaleNd === undefined) return false
        // Mismo criterio que el filtro de cajas del cobro.
        return caja.nd !== this.pointSaleNd
      },
      queueOpenTotalForCierre() {
        return this.queueOpenItemsForCierre.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
      },
    },

    methods: {
      setVisible(value) {
        this.$emit('update:visible', value)
      },
      // La vista decide cómo mostrar los avisos y el detalle de la cola.
      showToast(message, type = 'success') {
        this.$emit('notify', message, type)
      },
      showQueueErrors() {
        this.$emit('show-queue')
      },
      setCajasStatus(cajas = []) {
        this.$emit('cajas-loaded', cajas || [])
      },
      queueHasBlockingItemsForCaja(caja) {
        // queueState: dependencia reactiva para re-evaluar los bloqueos cuando cambia la cola.
        return this.queueState !== undefined && caja && this.isCashBlocked(caja)
      },
      checkCajaFlow() {
        serviceAPI.obtenerCajas().then(response => {
          const allCajas = response.data
          this.setCajasStatus(allCajas)
          const assignedCash = this.findAssignedCash(allCajas)

          this.cajaFlow.allCajas = allCajas

          if (!assignedCash) {
            this.setVisible(false)
            this.cajaFlow.cajas = []
            return
          }

          this.cajaFlow.cajas = [assignedCash]

          if (!assignedCash.only_open) {
            this.cajaFlow.mode = 'consulta'
            this.setVisible(false)
            this.cargarResumenCierre(assignedCash)
            return
          }

          this.cajaFlow.mode = 'apertura'
          this.setVisible(!assignedCash.opening)
          this.cargarResumenCierre(assignedCash)
        }).catch(error => {
          // Chequeo en segundo plano (también corre offline): no se avisa al operador.
          console.warn('[cashSession] checkCajaFlow error:', error)
        })
      },
      findAssignedCash(cajas = []) {
        const assignedCashId = this.assignedCashId
        if (!assignedCashId) return null
        return (cajas || []).find(c => c.id === assignedCashId && c.active) || null
      },
      abrirCaja(caja) {
        this.cajaFlow.loading = true
        serviceAPI.abrirCaja({ finances_cash_id: caja.id, amount: 0 })
          .then(() => {
            this.showToast(`Caja <b>${caja.name}</b> ABIERTA`, 'success')
            this.$emit('opened', caja)
            this.refreshCajasFlow({ keepOpen: true })
          })
          .catch(error => {
            this.showToast(ErrorHandler.showError(error), 'error')
            this.cajaFlow.loading = false
          })
      },
      cerrarCaja(caja, amount) {
        if (this.queueHasBlockingItemsForCaja(caja)) {
          this.showToast('No se puede cerrar caja: hay comprobantes pendientes de sincronización', 'warning')
          this.showQueueErrors()
          return
        }
        this.cajaFlow.loading = true
        serviceAPI.cerrarCaja({ finances_cash_id: caja.id, amount: amount || 0, transfer_labels: true })
          .then(() => {
            this.cajaFlow.modalRendicion.active = false
            this.showToast(`Caja <b>${caja.name}</b> CERRADA`, 'success')
            this.refreshCajasFlow()
          })
          .catch(error => {
            this.showToast(ErrorHandler.showError(error), 'error')
            this.cajaFlow.loading = false
          })
      },
      refreshCajasFlow(options = {}) {
        serviceAPI.obtenerCajas().then(response => {
          const allCajas = response.data
          this.setCajasStatus(allCajas)
          this.cajaFlow.allCajas = allCajas
          this.cajaFlow.cajas = this.cajaFlow.cajas.map(c => {
            const updated = allCajas.find(a => a.id === c.id)
            return updated || c
          })
          const cajasAbiertas = this.cajaFlow.cajas.filter(c => c.opening)
          const canContinue = this.cajaFlow.cajas.length === 0 || cajasAbiertas.length > 0
          this.cajaFlow.loading = false
          this.cajaFlow.cajas.forEach(caja => this.cargarResumenCierre(caja))
          if (options.keepOpen && canContinue) {
            this.cajaFlow.mode = 'cierre'
            this.setVisible(true)
          } else if (this.cajaFlow.mode === 'apertura' && canContinue) {
            setTimeout(() => { this.setVisible(false) }, 1000)
          }
        }).catch(error => {
          // La operación ya se hizo; sólo falló releer el estado de la caja.
          this.cajaFlow.loading = false
          this.showToast(`No se pudo actualizar el estado de la caja: ${ErrorHandler.showError(error)}`, 'error')
        })
      },
      confirmarAperturaCaja(caja) {
        this.cajaFlow.modalApertura.caja = caja
        this.cajaFlow.modalApertura.active = true
      },
      confirmarCierreCaja(caja, sinRendir = false) {
        this.cajaFlow.modalCierre.caja = caja
        this.cajaFlow.modalCierre.sinRendir = sinRendir
        this.cajaFlow.modalCierre.active = true
      },
      tieneSaldoParaRendir(caja) {
        return parseFloat(caja.total_available_ars || 0) > 0
      },
      abrirModalAgregarDinero(caja) {
        this.cajaFlow.modalDinero.caja = caja
        this.cajaFlow.modalDinero.reference = 'Fondo inicial'
        this.cajaFlow.modalDinero.amount = 0
        this.cajaFlow.modalDinero.active = true
        this.$nextTick(() => {
          if (this.$refs.inputDinero) this.$refs.inputDinero.focus()
        })
      },
      guardarDineroCaja() {
        if (!this.cajaFlow.modalDinero.reference) {
          this.showToast('Ingresá una referencia', 'warning'); return
        }
        if (parseFloat(this.cajaFlow.modalDinero.amount) <= 0) {
          this.showToast('Ingresá un importe mayor a cero', 'warning'); return
        }
        const caja = this.cajaFlow.modalDinero.caja
        let loader = this.$loading.show()
        const cajaPrincipal = caja.renders_to_cash_id
          ? this.cajaFlow.allCajas.find(c => c.id === caja.renders_to_cash_id)
          : null
        const promise = cajaPrincipal
          ? serviceAPI.agregarCajaDetalle({
              finances_cash_id: cajaPrincipal.id,
              finances_cash: { name: cajaPrincipal.name, type: cajaPrincipal.type },
              finances_cash_transfer_id: caja.id,
              finances_cash_transfer: { name: caja.name, type: caja.type },
              currency_id: 1,
              date: moment().format('YYYY-MM-DD'),
              time: moment().format('HH:mm:ss'),
              reference: this.cajaFlow.modalDinero.reference,
              amount: this.cajaFlow.modalDinero.amount,
              type_balance: '-',
              manual: true,
              event: 'transfer',
            })
          : serviceAPI.agregarCajaDetalle({
              finances_cash_id: caja.id,
              currency_id: 1,
              date: moment().format('YYYY-MM-DD'),
              time: moment().format('HH:mm:ss'),
              reference: this.cajaFlow.modalDinero.reference,
              amount: this.cajaFlow.modalDinero.amount,
              type_balance: '+',
              manual: true,
              event: 'adjustment',
            })
        promise.then(() => {
          loader.hide()
          this.cajaFlow.modalDinero.active = false
          this.showToast('Dinero agregado con éxito', 'success')
          this.refreshCajasFlow()
        }).catch(error => {
          loader.hide()
          this.showToast(ErrorHandler.showError(error), 'error')
        })
      },
      abrirModalRendicion(caja) {
        if (this.queueHasBlockingItemsForCaja(caja)) {
          this.showToast('No se puede cerrar caja: hay comprobantes pendientes de sincronización', 'warning')
          this.showQueueErrors()
          return
        }
        this.cajaFlow.modalRendicion.caja = caja
        this.cajaFlow.modalRendicion.amount = parseFloat(parseFloat(caja.total_available_ars || 0).toFixed(2))
        this.cajaFlow.modalRendicion.active = true
        this.$nextTick(() => {
          if (this.$refs.inputRendicion) this.$refs.inputRendicion.focus()
        })
      },
      guardarRendicion() {
        const amount = parseFloat(this.cajaFlow.modalRendicion.amount)
        if (amount === 0) {
          // Rendir 0 es cerrar sin transferir: se pide confirmación explícita.
          this.cajaFlow.modalRendicion.active = false
          this.confirmarCierreCaja(this.cajaFlow.modalRendicion.caja, true)
          return
        }
        if (!(amount > 0)) {
          this.showToast('Ingresá un importe mayor o igual a cero', 'warning'); return
        }
        const caja = this.cajaFlow.modalRendicion.caja
        this.cerrarCaja(caja, parseFloat(this.cajaFlow.modalRendicion.amount))
      },
      openCajaCierre() {
        if (!this.hasAssignedCash) return
        this.cajaFlow.loading = true
        serviceAPI.obtenerCajas().then(response => {
          const allCajas = response.data
          this.setCajasStatus(allCajas)
          const assignedCash = this.findAssignedCash(allCajas)
          this.cajaFlow.allCajas = allCajas
          if (!assignedCash) {
            this.setVisible(false)
            this.cajaFlow.cajas = []
            this.cajaFlow.loading = false
            return
          }
          this.cajaFlow.mode = assignedCash.only_open ? 'cierre' : 'consulta'
          this.setVisible(true)
          this.cajaFlow.cajas = [assignedCash]
          this.cajaFlow.loading = false
          this.cajaFlow.cajas.forEach(caja => this.cargarResumenCierre(caja))
        }).catch(error => {
          this.cajaFlow.loading = false
          this.showToast(`No se pudo consultar la caja: ${ErrorHandler.showError(error)}`, 'error')
        })
      },
      async cargarResumenCierre(caja) {
        this.$set(this.cajaFlow.resumen, caja.id, {
          loading: true,
          movimientos: [],
          resumenError: '',
        })

        const openDateTime = caja.only_open && caja.opening && caja.status && caja.status.open
        let movimientos = []
        let resumenError = ''

        if (openDateTime || !caja.only_open) {
          try {
            const params = {
              finances_cash_id: caja.id,
              date_start: moment().format('YYYY-MM-DD'),
              date_end: moment().format('YYYY-MM-DD'),
            }
            if (openDateTime) {
              const [openDate, openTime] = openDateTime.split(' ')
              params.date_start = openDate
              params.time_start = openTime
            }
            const res = await serviceAPI.obtenerCajaDetalles(params)
            const items = res.data.data || res.data || []
            movimientos = items
          } catch(e) {
            resumenError = 'No se pudieron cargar los movimientos del turno.'
            console.warn('[salesCounter] cierre movimientos error:', e)
          }
        }

        this.$set(this.cajaFlow.resumen, caja.id, {
          loading: false,
          movimientos,
          resumenError,
        })
      },
      showSaldoPeriodo(caja) {
        return !!(caja.opening || this.cajaFlow.mode === 'consulta')
      },
      saldoPeriodo(caja) {
        const resumen = this.cajaFlow.resumen[caja.id]
        return ((resumen && resumen.movimientos) || []).reduce((sum, mov) => {
          const amount = parseFloat(mov.amount || 0)
          return mov.type_balance === '-' ? sum - amount : sum + amount
        }, 0)
      },
      formatCurrency(amount) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount || 0)
      },
      formatDateTimeHMS(dateTime) {
        if (!dateTime) return ''
        return moment(dateTime).format('HH:mm:ss')
      },
      formatCajaResumenDate(movimientos) {
        const fechas = (movimientos || [])
          .filter(movimiento => movimiento && movimiento.date_time)
          .map(movimiento => moment(movimiento.date_time).format('DD/MM/YYYY'))
          .filter((fecha, index, lista) => lista.indexOf(fecha) === index)
        if (!fechas.length) return moment().format('DD/MM/YYYY')
        return fechas.length === 1 ? fechas[0] : fechas[0] + ' – ' + fechas[fechas.length - 1]
      },
      avatarFromName(name) {
        return Funciones.avatarFromName(name)
      },
      cajaMovimientoContraparte(movimiento) {
        const origen = movimiento.origen || {}
        return origen.customer || origen.supplier || ''
      },
      cajaMovimientoContraparteTipo(movimiento) {
        const origen = movimiento.origen || {}
        if (origen.customer) return 'cliente'
        if (origen.supplier) return 'proveedor'
        return ''
      },
      cajaMovimientoContraparteImage(movimiento) {
        const origen = movimiento.origen || {}
        return origen.customer_image || origen.supplier_image || ''
      },
    },
  }
</script>

<style scoped>
  /* Movimientos de caja: concepto y contraparte se reparten el ancho libre (3:2). */
  .sc-cierre-cpbt-header.sc-caja-mov-grid,
  .sc-cierre-cpbt-row.sc-caja-mov-grid {
    grid-template-columns: 90px minmax(220px, 3fr) minmax(200px, 2fr) 150px;
  }
  .sc-saldo-split {
    display: flex;
    justify-content: space-around;
    gap: 16px;
  }
  /* ── Cierre de caja — resumen ───────────────────────────── */
  .sc-caja-screen {
    height: calc(100vh - 132px);
    min-height: 320px;
    overflow: hidden;
  }
  .sc-caja-row,
  .sc-caja-col,
  .sc-caja-card {
    height: 100%;
    min-height: 0;
  }
  .sc-caja-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .sc-caja-card >>> .card-header {
    flex: 0 0 auto;
  }
  .sc-caja-body {
    padding: 16px 16px 130px;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .sc-caja-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .sc-caja-card-open  >>> .card-header { background: #f0fdf4; color: #166534; border-bottom: 2px solid #28a745; }
  .sc-caja-card-closed >>> .card-header { background: #fff5f5; color: #991b1b; border-bottom: 2px solid #dc3545; }
  .sc-caja-card-info >>> .card-header { background: #eef8ff; color: #075985; border-bottom: 2px solid #0ea5e9; }

  .sc-caja-mode-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.8;
    margin-bottom: 4px;
  }
  .sc-action-grid {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    background: #fff;
    padding: 12px 16px;
    box-shadow: 0 -2px 8px rgba(0,0,0,0.12);
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
  }
  .sc-action-block {
    border-radius: 10px;
    padding: 14px 16px;
    text-align: center;
    cursor: pointer;
    transition: filter 0.15s;
    user-select: none;
  }
  .sc-action-block:hover:not(.sc-action-disabled) {
    filter: brightness(0.9);
  }
  .sc-action-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .sc-action-icon {
    font-size: 1.5rem;
    display: block;
    margin-bottom: 6px;
  }
  .sc-action-title {
    font-weight: 800;
    font-size: 1.05rem;
    line-height: 1.2;
  }
  .sc-action-sub {
    font-size: 0.82rem;
    margin-top: 5px;
    opacity: 0.88;
    line-height: 1.35;
  }
  .sc-action-success  { background: #28a745; color: #fff; }
  .sc-action-money    { background: #0f766e; color: #fff; }
  .sc-action-warn     { background: #e0a800; color: #fff; }
  .sc-action-secondary{ background: #495057; color: #fff; }
  .sc-action-danger   { background: #dc3545; color: #fff; }
  .sc-action-outline  { background: #fff; color: #343a40; border: 2px solid #343a40; }

  .sc-saldo-block {
    background: #6d28d9;
    border-radius: 10px;
    padding: 16px 20px;
    text-align: center;
  }
  .sc-saldo-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255,255,255,0.75);
    font-weight: 600;
    margin-bottom: 4px;
  }
  .sc-saldo-value {
    font-size: 2rem;
    font-weight: 800;
    color: #fff;
    line-height: 1;
  }
  .sc-cierre-cpbt-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 13px;
  }
  .sc-cierre-cpbt-header {
    display: grid;
    grid-template-columns: 1fr 110px 150px;
    gap: 16px;
    padding: 6px 12px;
    background: #343a40;
    color: #adb5bd;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    position: sticky;
    top: 0;
  }
  .sc-cierre-cpbt-row {
    display: grid;
    grid-template-columns: 1fr 110px 150px;
    gap: 16px;
    padding: 7px 12px;
    border-bottom: 1px solid #f0f0f0;
    align-items: center;
  }
  .sc-cierre-cpbt-row:last-child { border-bottom: none; }
  .sc-cierre-cpbt-row:hover { background: #f8f9fa; }
  .sc-cierre-cpbt-total { font-weight: 700; text-align: right; white-space: nowrap; }
  .sc-caja-counterparty {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
  }
  .sc-caja-counterparty__type {
    display: block;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.1;
    text-transform: uppercase;
    letter-spacing: .04em;
  }
  .sc-caja-counterparty__name {
    display: block;
    overflow: hidden;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sc-caja-counterparty--cliente .sc-caja-counterparty__type { color: #0c5460; }
  .sc-caja-counterparty--proveedor .sc-caja-counterparty__type { color: #6f42c1; }
  @media (max-width: 575.98px) {
    .sc-saldo-split {
      flex-direction: column;
      gap: 10px;
    }
    .sc-saldo-split .sc-saldo-value {
      font-size: 1.6rem;
    }
    /* Mobile: la pantalla fluye con la página (la cabecera es más alta que en desktop)
       y la botonera fija ocupa una sola fila compacta. */
    .sc-caja-screen,
    .sc-caja-row,
    .sc-caja-col,
    .sc-caja-card {
      height: auto;
      overflow: visible;
    }
    .sc-caja-body {
      padding: 12px 12px 96px;
      overflow: visible;
    }
    .sc-action-grid {
      grid-template-columns: none;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
      gap: 6px;
      padding: 8px;
    }
    .sc-action-block {
      padding: 8px 4px;
    }
    .sc-action-icon {
      font-size: 1.1rem;
      margin-bottom: 2px;
    }
    .sc-action-title {
      font-size: 0.82rem;
    }
    .sc-action-sub {
      display: none;
    }
    /* Movimientos como tarjetas: concepto + importe arriba, hora + contraparte abajo. */
    .sc-cierre-cpbt-header.sc-caja-mov-grid {
      display: none;
    }
    .sc-cierre-cpbt-row.sc-caja-mov-grid {
      grid-template-columns: auto minmax(0, 1fr) auto;
      grid-template-areas:
        "concepto concepto importe"
        "hora contra contra";
      row-gap: 4px;
      column-gap: 8px;
    }
    .sc-caja-mov-grid > :nth-child(1) { grid-area: hora; font-size: 0.75rem; }
    .sc-caja-mov-grid > :nth-child(2) { grid-area: concepto; min-width: 0; overflow-wrap: anywhere; }
    .sc-caja-mov-grid > :nth-child(3) { grid-area: contra; min-width: 0; }
    .sc-caja-mov-grid > :nth-child(4) { grid-area: importe; }
  }
</style>

<style>
.sc-modal-money-header {
  background: #0f766e !important;
  color: #fff !important;
}

.sc-btn-money {
  background: #0f766e !important;
  border-color: #0d665f !important;
  color: #fff !important;
}

.sc-btn-money:hover:not(:disabled) {
  background: #0d665f !important;
  border-color: #0a544e !important;
}
</style>
